//src/components/FormSettings/FormDesignLayout/SubmitButtonComponent.jsx
import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { useFormSubmissions } from '../../../contexts/FormSubmissionsContext'; //  Use FormSubmissionsContext instead of FormActionsContext
import { useAuth } from '../../../contexts/AuthContext';
import { useFormTheme } from '../../../contexts/FormThemeContext';
import { useFormLayout } from '../../../contexts/FormLayoutContext';
import { useFieldEdit } from '../../../contexts/FieldEditContext';
import { useFormAnswers } from '../../../contexts/FormAnswersContext';

const debugMode = process.env.NODE_ENV === 'development'; // Enable debug mode conditionally

const SubmitButtonComponent = ({
  onClick,
  /** AUF-only: when true, show spinner, swap text, disable button */
  loading = false,
  /** AUF-only: label while loading (spinner will be prepended) */
  loadingLabel = 'Submitting',
  /** Optional external disable in addition to internal validation */
  disabled: externalDisabled = false,
  /** Optional label override (fallback remains theme label) */
  label,
}) => {

    const { handleSubmit } = useFormSubmissions(); // ✅ Use FormSubmissionsContext
    const { currentUser } = useAuth();
    const { submitButtonStyle } = useFormTheme();
    const { layoutConfig, fieldsMetadata } = useFormLayout();
    const { fieldChanges, validationErrors } = useFieldEdit();  // Hook into validationErrors
    const { buildSubmissionArray, getAnswersSnapshot } = useFormAnswers() || {};

    const [isHovered, setIsHovered] = useState(false); // Track hover state
    const [isClicked, setIsClicked] = useState(false); // Track click state
//    const [hasValidationErrors, setHasValidationErrors] = useState(false); // Track if there are validation errors

// Function to darken a color for the hover effect
const darkenColor = (color, percent) => {
  const num = parseInt(color.replace('#', ''), 16),
    amt = Math.round(2.55 * percent * 100),
    R = (num >> 16) + amt,
    G = (num >> 8 & 0x00FF) + amt,
    B = (num & 0x0000FF) + amt;
  return `#${(
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  )
    .toString(16)
    .slice(1)}`;
};

/* ──────────────────────────────────────────────────────────────
   answersToken:
   - We avoid calling a function inside the dependency array below.
   - Instead, we compute a lightweight “change token” once here.
   - When the AnswersContext map changes, getAnswersSnapshot()
     will return a new reference → this token changes → memo re-runs.
   ────────────────────────────────────────────────────────────── */
const answersToken = useMemo(
  () => (typeof getAnswersSnapshot === 'function' ? getAnswersSnapshot() : null),
  [getAnswersSnapshot]
);

// Handle collected fields data, ensuring value handling for both strings and arrays
const collectedFieldsData = useMemo(() => {
  // Prefer canonical list from AnswersContext (UFI answers + defaults; ordered by layout)
  if (typeof buildSubmissionArray === 'function') {
    const arr = buildSubmissionArray(); // [{ id, ufi, type, label, value, required }]
//  if (debugMode) console.log('[SubmitButtonComponent] Collected fields (canonical):', arr);
    return arr;
  }

  // Fallback to previous derivation if AnswersContext isn’t mounted
  const collectedData = Object.values(layoutConfig)
    .flat()
    .filter(field => field.type !== 'submit')
    .map(field => {
      const fieldId = field.id;
      const fm = fieldsMetadata[fieldId] || {};
      const fc = fieldChanges[fieldId] || {};

      const label = fc.label || fm.label || field.label || fieldId;
      const rawValue = fc.defaultValue ?? fm.defaultValue ?? '';

      // Keep arrays as arrays; don’t stringify here
      const normalizedValue = Array.isArray(rawValue) ? rawValue : rawValue;

      return {
        id: fieldId,
        ufi: fm.ufi || fieldId,
        label,
        value: normalizedValue,
        type: fm.type || field.type, // include type for type-aware emptiness
        required: field.required || fm.required || false,
      };
    });
// if (debugMode) console.log('[SubmitButtonComponent] Collected fields (fallback):', collectedData);
  return collectedData;
// Re-evaluate when answer map changes so the button updates while typing/choosing
}, [
  layoutConfig,
  fieldsMetadata,
  fieldChanges,
  buildSubmissionArray,
  answersToken // ← tokenized dependency instead of calling a function in the deps
]);


//    useEffect(() => {
//        if (debugMode) {
//            console.log('[SubmitButtonComponent] Initialized with collected fields:', collectedFieldsData);
//        }
//    }, [collectedFieldsData]);

    const handleFormSubmit = useCallback(async () => {
//        console.log('--- [SubmitButtonComponent] Form submission initiated ---');
//        console.log('[SubmitButtonComponent] Current user:', currentUser);

        const userId = currentUser?.uid;

        try {
            await handleSubmit(collectedFieldsData, { userId });
//            console.log('[SubmitButtonComponent] handleSubmit called successfully with:', { fields: collectedFieldsData, userId });
        } catch (error) {
            console.error('[SubmitButtonComponent] Error during handleSubmit:', error);
            // alert('An error occurred while finalizing the form submission. Please try again.');
        }

//        console.log('--- [SubmitButtonComponent] Form submission process completed ---');
    }, [handleSubmit, collectedFieldsData, currentUser]);

    const containerStyle = useMemo(() => ({
        display: 'flex',
        justifyContent: submitButtonStyle.alignment === 'center' ? 'center' :
            submitButtonStyle.alignment === 'right' ? 'flex-end' : 'flex-start',
        width: '100%',
        padding: '10px 0',
    }), [submitButtonStyle.alignment]);

// Determine if any required field is empty (type-aware; ignore hidden/disabled)
const isMissingRequired = useMemo(() => {
  const isEmpty = (t, v) => {
    if (t === 'multi-select') return !Array.isArray(v) || v.length === 0;
    if (t === 'attach')       return v == null;
    return String(v ?? '').trim() === '';
  };
  const isEffectivelyRequired = (item) => {
    const meta = fieldsMetadata[item.id] || {};
    if (meta.hidden)   return false; // hidden shouldn’t block
    if (meta.disabled) return false; // hard-disabled shouldn’t block
    return !!item.required;
  };
  return collectedFieldsData.some(
    f => isEffectivelyRequired(f) && isEmpty((f.type || (fieldsMetadata[f.id]?.type)), f.value)
  );
}, [collectedFieldsData, fieldsMetadata]);


    // Determine if we have any validation errors
    const hasValidationErrors = useMemo(() => {
        // If your validationErrors is an object like { fieldId: 'Error message' }, check if any value is truthy
        return Object.values(validationErrors || {}).some(Boolean);
    }, [validationErrors]);

    // Combine both to decide if the form is invalid
    const isFormInvalid = useMemo(() => {
        return isMissingRequired || hasValidationErrors;
    }, [isMissingRequired, hasValidationErrors]);


  // Final disabled used by the button: internal validity OR external OR loading
  const finalDisabled = isFormInvalid || externalDisabled || loading;

    const buttonStyles = useMemo(() => {
        // When disabled (invalid / external / loading), lock background to base
        // and mute the button for clear affordance.
        const canHover = !finalDisabled;

        const baseBackground = submitButtonStyle.backgroundColor;
        const hoverBackground =
            submitButtonStyle.hoverBackgroundColor ||
            darkenColor(submitButtonStyle.backgroundColor, 0.1);

        const effectiveBackground = canHover && isHovered
            ? hoverBackground
            : baseBackground;

        return {
            backgroundColor: effectiveBackground,
            color: submitButtonStyle.textColor,
            fontSize: submitButtonStyle.fontSize,
            fontWeight: submitButtonStyle.fontWeight,
            padding: '10px 20px',
            border: `${submitButtonStyle.borderWidth} ${submitButtonStyle.borderStyle} ${submitButtonStyle.borderColor}`,
            borderRadius: submitButtonStyle.borderRadius,
            width: submitButtonStyle.width,
            height: submitButtonStyle.height,

            // Keep your pressed animation only when interactive
            transform: !finalDisabled && isClicked ? 'scale(0.98)' : 'scale(1)',
            transition: 'all 0.2s ease-in-out',

            // If form is invalid => show "disabled" look
            // (now expanded: invalid OR externally disabled OR loading)
            ...(finalDisabled
                ? {
                    cursor: 'not-allowed',
                    opacity: 0.48, // Slightly dim the button
                }
                : {
                    cursor: 'pointer',
                }
            ),
        };
    }, [
        submitButtonStyle,
        isHovered,
        isClicked,
        finalDisabled,
        darkenColor,
    ]);


    return (
        <div style={containerStyle}>
            <button
                type="submit"
                // prevent clicks when disabled; otherwise use onClick or fallback
                onClick={(e) => {
                    if (finalDisabled) { e.preventDefault(); return; }
                    if (onClick) return onClick(e);
                    return handleFormSubmit();
                }}
                style={buttonStyles}
                onMouseEnter={() => { if (!finalDisabled) setIsHovered(true); }}
                onMouseLeave={() => setIsHovered(false)}
                onMouseDown={() => { if (!finalDisabled) setIsClicked(true); }}
                onMouseUp={() => setIsClicked(false)}
                //  use the unified disabled flag
                disabled={finalDisabled}
                // a11y hints while we’re submitting
                aria-busy={loading ? 'true' : 'false'}
                aria-disabled={finalDisabled ? 'true' : 'false'}
            >
                {loading ? (
                  // Inline loader + text INSIDE the disabled button
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      // keep text readable even when opacity is lowered
                      color: submitButtonStyle.textColor,
                    }}
                    aria-live="polite"
                    role="status"
                  >
                    <CircularProgress size={14} />
                    {loadingLabel || 'Submitting'}
                  </span>
                ) : (
                  // Normal label (with optional external override)
                  (label || submitButtonStyle.label || 'Submit')
                )}
            </button>
        </div>
    );

};

export default SubmitButtonComponent;
